/**
 * analyticsView.js — Cruscotto live, in sola lettura, dell'asta attiva.
 */

import { getRoleChipClass } from '../features/filters/index.js';
import { escapeHtml } from '../utils/escapeHtml.js';
import { playerToKey } from '../utils/playerKey.js';
import { EXIT_MODE_ASSIGNED } from '../services/db/auctions.pocketbase.js';
import { computeAnalyticsSummary, TOP_AUCTION_PRICES } from './analyticsCalc.js';
import {
  SERIES_ID_GLOBAL,
  SERIES_ID_ME,
  SERIES_ID_UNASSIGNED,
  TREND_VIEW_GLOBAL,
  TREND_VIEW_ROLES,
  TREND_VIEW_ROSA,
  TURN_AXIS_STEP,
  buildPriceTrendSvg,
  collectMarketEvents,
  colorForRoleTrendSeries,
  colorForSeriesId,
  computePriceTrend,
  computePriceTrendSeries,
  computePriceTrendSeriesByRole,
  listPriceTrendRoleBuckets,
  normalizeGlobalTurnRange,
  slicePriceTrend,
} from './analyticsPriceTrend.js';
import { opponentPlaceholder } from './auctionOpponents.js';
import { buildOpponentSpendRows, buildOwnSpendRow, creditBatteryLevel, richestCreditBenchmark, sortPerRosaRows } from './opponentSpend.js';
import { buildRosterAcquisitionGrid, renderRosterAcquisitionGrid } from './analyticsRosterGrid.js';

function formatPrice(value) {
  return value == null ? 'n/d' : `${Number(value)}M`;
}

function roleChipClass(bucket) {
  const role = String(bucket || '').replace(/\+$/, '');
  const classicRole = role === 'D' ? 'DC' : role;
  return getRoleChipClass(classicRole);
}

function renderRoleChip(bucket) {
  if (!bucket) return '';
  return `<span class="analytics-role-chip ${roleChipClass(bucket)}">${escapeHtml(bucket)}</span>`;
}

function renderTable({ label, headings, rows, testId }) {
  return `
    <div class="analytics-table-scroll">
      <table class="analytics-table" data-testid="${testId}">
        <caption class="analytics-sr-only">${escapeHtml(label)}</caption>
        <thead>
          <tr>${headings.map((heading) => `<th scope="col">${escapeHtml(heading)}</th>`).join('')}</tr>
        </thead>
        <tbody>${rows.join('')}</tbody>
      </table>
    </div>
  `;
}

function renderListPanel({ headings, itemsHtml, listTag, listClass, headModifier = '' }) {
  const headCells = headings.map((label, index) => {
    const numCol = index >= 2;
    return `<span class="analytics-list-head-cell${numCol ? ' is-num' : ''}">${escapeHtml(label)}</span>`;
  }).join('');
  return `
    <div class="analytics-list-panel">
      <div class="analytics-list-head${headModifier ? ` ${headModifier}` : ''}" role="row" aria-hidden="true">
        ${headCells}
      </div>
      <${listTag} class="${listClass}">${itemsHtml}</${listTag}>
    </div>`;
}

function renderSituation(situation, { title = 'Situazione mia' } = {}) {
  return `
    <section class="analytics-situation" data-testid="analytics-situation" aria-labelledby="analytics-situation-title">
      <h2 id="analytics-situation-title" class="analytics-section-title">${escapeHtml(title)}</h2>
      <div class="analytics-situation-stats">
        <div class="analytics-stat">
          <span>Residuo</span>
          <strong class="num">${formatPrice(situation.residui)}</strong>
        </div>
        <div class="analytics-stat">
          <span>Presi</span>
          <strong class="num">${situation.owned}/${situation.slots}</strong>
        </div>
        <div class="analytics-stat">
          <span>Media per posto libero</span>
          <strong class="num">${formatPrice(situation.perOpenSlot)}</strong>
        </div>
      </div>
    </section>
  `;
}

function renderMarket(market) {
  if (market.top.length === 0) {
    return `
      <section class="analytics-section" aria-labelledby="analytics-market-title">
        <h2 id="analytics-market-title" class="analytics-section-title">Mercato</h2>
        <p class="analytics-empty" data-testid="analytics-market-empty">
          Le medie di mercato appariranno dopo i primi acquisti registrati con prezzo.
        </p>
      </section>
    `;
  }

  const populatedRows = market.rows.filter((row) => row.count > 0);
  const table = populatedRows.length > 0
    ? renderTable({
      label: 'Prezzi di mercato per ruolo',
      headings: ['Ruolo', 'Media', 'Pezzi', 'Min–max'],
      rows: populatedRows.map((row) => `
        <tr>
          <th scope="row">${renderRoleChip(row.bucket)}</th>
          <td class="num">${formatPrice(row.avg)}</td>
          <td class="num">${row.count}</td>
          <td class="num">${formatPrice(row.min)}–${formatPrice(row.max)}</td>
        </tr>
      `),
      testId: 'analytics-market-table',
    })
    : '<p class="analytics-inline-note">Ruoli non disponibili per i prezzi registrati.</p>';

  const top = market.top.map((entry) => `
    <li class="analytics-top-item${entry.mine ? ' analytics-top-item--me' : ''}">
      ${renderRoleChip(entry.buckets[0])}
      <span class="analytics-top-name">${escapeHtml(entry.name)}</span>
      <strong class="analytics-top-price num">${formatPrice(entry.price)}</strong>
    </li>
  `).join('');

  return `
    <section class="analytics-section" aria-labelledby="analytics-market-title">
      <div class="analytics-section-heading analytics-section-heading--split">
        <h2 id="analytics-market-title" class="analytics-section-title">Mercato</h2>
        <span class="analytics-section-meta">Top ${TOP_AUCTION_PRICES} acquisti</span>
      </div>
      <div class="analytics-market-grid">
        ${table}
        <div class="analytics-top">
          ${renderListPanel({
            headings: ['Ruolo', 'Giocatore', 'Prezzo'],
            itemsHtml: top,
            listTag: 'ol',
            listClass: 'analytics-top-list',
          })}
        </div>
      </div>
    </section>
  `;
}

function renderTrendRoleFilters(roleBuckets, roleFilter) {
  if (!roleBuckets.length) return '';
  const allActive = !roleFilter;
  const chips = [
    `<button type="button" class="analytics-trend-role-btn${allActive ? ' is-active' : ''}"
            data-analytics-trend-role=""
            aria-pressed="${allActive}">
      Tutti
    </button>`,
    ...roleBuckets.map(({ bucket, count }) => {
      const on = roleFilter === bucket;
      return `<button type="button"
              class="analytics-trend-role-btn analytics-role-chip ${roleChipClass(bucket)}${on ? ' is-active' : ''}"
              data-analytics-trend-role="${escapeHtml(bucket)}"
              aria-pressed="${on}">
        ${escapeHtml(bucket)}<span class="analytics-trend-role-count num">${count}</span>
      </button>`;
    }),
  ].join('');
  return `
    <div class="analytics-trend-filters" role="group" aria-label="Filtra grafico per ruolo" data-testid="analytics-trend-role-filters">
      ${chips}
    </div>`;
}

function buildTrendSeriesChips({ opponents, exitEntries }) {
  const chips = [
    { id: SERIES_ID_ME, label: 'Io' },
    ...opponents.map((opponent, index) => ({
      id: opponent.id,
      label: String(opponent.name || '').trim() || opponentPlaceholder(index),
    })),
  ];
  const hasUnassigned = exitEntries.some((entry) => !entry.opponentId);
  if (hasUnassigned) {
    chips.push({ id: SERIES_ID_UNASSIGNED, label: 'Da assegnare' });
  }
  return chips;
}

function resolveTrendViewMode(state, assigned) {
  if (!assigned) return TREND_VIEW_GLOBAL;
  if (state.analyticsTrendViewMode === TREND_VIEW_GLOBAL
    || state.analyticsTrendViewMode === TREND_VIEW_ROSA
    || state.analyticsTrendViewMode === TREND_VIEW_ROLES) {
    return state.analyticsTrendViewMode;
  }
  if (Array.isArray(state.analyticsTrendSeriesIds)
    && state.analyticsTrendSeriesIds.includes(SERIES_ID_GLOBAL)) {
    return TREND_VIEW_GLOBAL;
  }
  return TREND_VIEW_ROSA;
}

function normalizeTrendSeriesIds(selectedIds, validIds) {
  const valid = new Set(validIds);
  const next = (Array.isArray(selectedIds) ? selectedIds : [])
    .filter((id) => valid.has(id) && id !== SERIES_ID_GLOBAL);
  return next.length ? next : [...validIds];
}

function normalizeTrendRoleSeriesIds(selectedIds, validIds) {
  const valid = new Set(validIds);
  const next = (Array.isArray(selectedIds) ? selectedIds : [])
    .filter((id) => valid.has(id));
  return next.length ? next : [...validIds];
}

function renderTrendViewModeSwitch(activeMode) {
  const modes = [
    { id: TREND_VIEW_GLOBAL, label: 'Globale' },
    { id: TREND_VIEW_ROSA, label: 'Rose' },
    { id: TREND_VIEW_ROLES, label: 'Ruoli' },
  ];
  return `
    <div class="analytics-trend-view-switch" role="tablist" aria-label="Tipo di grafico"
         data-testid="analytics-trend-view-switch">
      ${modes.map((mode) => `
        <button type="button" role="tab"
          class="analytics-trend-view-btn${activeMode === mode.id ? ' is-active' : ''}"
          data-analytics-trend-view="${escapeHtml(mode.id)}"
          aria-selected="${activeMode === mode.id}">
          ${escapeHtml(mode.label)}
        </button>
      `).join('')}
    </div>`;
}

function renderTrendRoleSeriesFilters(roleBuckets, selectedIds) {
  if (!roleBuckets.length) return '';
  const selected = new Set(selectedIds);
  const buttons = roleBuckets.map(({ bucket, count }) => {
    const on = selected.has(bucket);
    return `<button type="button"
            class="analytics-trend-series-btn analytics-role-chip ${roleChipClass(bucket)}${on ? ' is-active' : ''}"
            data-analytics-trend-role-series="${escapeHtml(bucket)}"
            aria-pressed="${on}">
      ${escapeHtml(bucket)}<span class="analytics-trend-role-count num">${count}</span>
    </button>`;
  }).join('');
  return `
    <div class="analytics-trend-series-filters" role="group" aria-label="Confronta ruolo"
         data-testid="analytics-trend-role-series-filters">
      <span class="analytics-trend-series-label">Confronta ruolo</span>
      ${buttons}
    </div>`;
}

function renderTrendSeriesFilters(chips, selectedIds) {
  if (!chips?.length) return '';
  const selected = new Set(selectedIds);
  const buttons = chips.map((chip) => {
    const on = selected.has(chip.id);
    return `<button type="button" class="analytics-trend-series-btn${on ? ' is-active' : ''}"
            data-analytics-trend-series="${escapeHtml(chip.id)}"
            aria-pressed="${on}">${escapeHtml(chip.label)}</button>`;
  }).join('');
  return `
    <div class="analytics-trend-series-filters" role="group" aria-label="Confronta rosa"
         data-testid="analytics-trend-series-filters">
      <span class="analytics-trend-series-label">Confronta rosa</span>
      ${buttons}
    </div>`;
}

function buildTurnScaleMarks(total) {
  if (total < 2) return [1];
  const marks = [1];
  for (let v = TURN_AXIS_STEP; v < total; v += TURN_AXIS_STEP) {
    marks.push(v);
  }
  if (marks[marks.length - 1] !== total) marks.push(total);
  return marks;
}

function turnRangeFillStyle(from, to, total) {
  if (total <= 1) return { left: '0%', width: '100%' };
  const left = ((from - 1) / (total - 1)) * 100;
  const width = ((to - from) / (total - 1)) * 100;
  return { left: `${left}%`, width: `${Math.max(0, width)}%` };
}

function renderTrendTurnRange({ from, to, total }) {
  if (total < 2) return '';
  const fill = turnRangeFillStyle(from, to, total);
  const marks = buildTurnScaleMarks(total);
  const scaleHtml = marks.map((mark) => {
    const pct = total <= 1 ? 0 : ((mark - 1) / (total - 1)) * 100;
    return `<span class="analytics-trend-turn-range-mark" style="left:${pct}%"><i>${mark}</i></span>`;
  }).join('');

  return `
    <div class="analytics-trend-turn-range" data-testid="analytics-trend-turn-range">
      <div class="analytics-trend-turn-range-head">
        <span class="analytics-trend-turn-range-label">Intervallo chiamate</span>
        <div class="analytics-trend-turn-range-badges">
          <span class="analytics-trend-turn-range-badge">Da <strong class="num" data-analytics-turn-from-out>${from}</strong></span>
          <span class="analytics-trend-turn-range-badge">A <strong class="num" data-analytics-turn-to-out>${to}</strong></span>
          <button type="button" class="analytics-trend-turn-range-reset" data-analytics-turn-reset>Tutto l&apos;asta</button>
        </div>
      </div>
      <div class="analytics-trend-turn-range-track-wrap">
        <div class="analytics-trend-turn-range-track" data-turn-range-track>
          <div class="analytics-trend-turn-range-rail" aria-hidden="true"></div>
          <div class="analytics-trend-turn-range-fill" data-turn-range-fill
               style="left:${fill.left};width:${fill.width}"></div>
          <input type="range" class="analytics-trend-turn-range-input analytics-trend-turn-range-input--from"
                 min="1" max="${total}" value="${from}" step="1"
                 data-analytics-turn-from aria-label="Prima chiamata dell'intervallo">
          <input type="range" class="analytics-trend-turn-range-input analytics-trend-turn-range-input--to"
                 min="1" max="${total}" value="${to}" step="1"
                 data-analytics-turn-to aria-label="Ultima chiamata dell'intervallo">
        </div>
        <div class="analytics-trend-turn-range-scale" aria-hidden="true">${scaleHtml}</div>
      </div>
    </div>`;
}

function syncTurnRangeVisuals(root, from, to, total) {
  const fill = root.querySelector('[data-turn-range-fill]');
  const fromOut = root.querySelector('[data-analytics-turn-from-out]');
  const toOut = root.querySelector('[data-analytics-turn-to-out]');
  const style = turnRangeFillStyle(from, to, total);
  if (fill) {
    fill.style.left = style.left;
    fill.style.width = style.width;
  }
  if (fromOut) fromOut.textContent = String(from);
  if (toOut) toOut.textContent = String(to);
}

function renderSingleSeriesLegend() {
  return `
        <div class="analytics-trend-legend" aria-hidden="true">
          <span class="analytics-trend-legend-item"><i class="analytics-trend-swatch analytics-trend-swatch--price"></i> Prezzo</span>
          <span class="analytics-trend-legend-item"><i class="analytics-trend-swatch analytics-trend-swatch--avg"></i> Media cumulativa</span>
          <span class="analytics-trend-legend-item"><i class="analytics-trend-swatch analytics-trend-swatch--mine"></i> Preso da me</span>
          <span class="analytics-trend-legend-item"><i class="analytics-trend-swatch analytics-trend-swatch--others"></i> Preso da altri</span>
        </div>`;
}

function renderCompareSeriesLegend(seriesChips, selectedIds, opponentsOrderedIds) {
  const selected = new Set(selectedIds);
  const items = (seriesChips || [])
    .filter((chip) => selected.has(chip.id))
    .map((chip) => {
      const color = colorForSeriesId(chip.id, opponentsOrderedIds);
      return `<span class="analytics-trend-legend-item"><i class="analytics-trend-swatch analytics-trend-swatch--series" style="background:${color}"></i> ${escapeHtml(chip.label)}</span>`;
    })
    .join('');
  return `<div class="analytics-trend-legend" aria-hidden="true">${items}</div>`;
}

function renderRoleSeriesLegend(roleBuckets, selectedIds, mode = 'mantra') {
  const selected = new Set(selectedIds);
  const items = roleBuckets
    .filter(({ bucket }) => selected.has(bucket))
    .map(({ bucket }) => {
      const color = colorForRoleTrendSeries(bucket, mode);
      return `<span class="analytics-trend-legend-item"><i class="analytics-trend-swatch analytics-trend-swatch--series" style="background:${color}"></i> ${escapeHtml(bucket)}</span>`;
    })
    .join('');
  return `<div class="analytics-trend-legend" aria-hidden="true">${items}</div>`;
}

function renderPriceTrend(trend, {
  roleBuckets = [],
  roleFilter = null,
  assigned = false,
  trendViewMode = TREND_VIEW_GLOBAL,
  seriesChips = null,
  selectedSeriesIds = [],
  roleSeriesIds = [],
  opponentsOrderedIds = [],
  globalTurnRange = null,
  mode = 'mantra',
} = {}) {
  const hasAnyData = roleBuckets.length > 0;

  if (!hasAnyData) {
    return `
      <section class="analytics-section" aria-labelledby="analytics-trend-title">
        <h2 id="analytics-trend-title" class="analytics-section-title">Andamento prezzi</h2>
        <p class="analytics-empty" data-testid="analytics-trend-empty">
          Il grafico apparirà dopo il primo acquisto registrato con prezzo.
        </p>
      </section>
    `;
  }

  const isGlobalTrend = trendViewMode === TREND_VIEW_GLOBAL;
  const isRolesTrend = trendViewMode === TREND_VIEW_ROLES;
  const isRosaTrend = trendViewMode === TREND_VIEW_ROSA;

  const filters = (isGlobalTrend || isRosaTrend)
    ? renderTrendRoleFilters(roleBuckets, roleFilter)
    : '';
  const viewSwitch = assigned ? renderTrendViewModeSwitch(trendViewMode) : '';
  const seriesFilters = assigned && isRosaTrend
    ? renderTrendSeriesFilters(seriesChips, selectedSeriesIds)
    : '';
  const roleSeriesFilters = assigned && isRolesTrend
    ? renderTrendRoleSeriesFilters(roleBuckets, roleSeriesIds)
    : '';
  const turnRangeControl = isGlobalTrend && globalTurnRange
    ? renderTrendTurnRange(globalTurnRange)
    : '';
  const series = assigned && (isRosaTrend || isRolesTrend) ? (trend.series || []) : null;
  const hasPoints = isGlobalTrend || !assigned
    ? trend.points.length > 0
    : series.some((row) => row.points.length);

  if (!hasPoints) {
    const label = roleFilter ? `Ruolo ${roleFilter}` : 'Tutti i ruoli';
    return `
      <section class="analytics-section analytics-section--trend" aria-labelledby="analytics-trend-title">
        <div class="analytics-section-heading">
          <h2 id="analytics-trend-title" class="analytics-section-title">Andamento prezzi</h2>
          <span class="analytics-section-meta">${escapeHtml(label)}</span>
        </div>
        ${filters}
        ${viewSwitch}
        ${seriesFilters}
        ${roleSeriesFilters}
        <p class="analytics-empty" data-testid="analytics-trend-filter-empty">
          Nessun acquisto per questo ruolo. Prova un altro filtro o «Tutti».
        </p>
      </section>
    `;
  }

  const filterLabel = roleFilter ? ` · ${roleFilter}` : '';
  const multiCompare = assigned && (isRosaTrend || isRolesTrend) && series.length >= 2;
  let meta;
  let ariaLabel;
  if (multiCompare && isRolesTrend) {
    const totalPoints = series.reduce((sum, row) => sum + row.points.length, 0);
    meta = `${totalPoints} punti · ${series.length} ruoli`;
    ariaLabel = `Confronto andamento prezzi per ruolo su ${totalPoints} acquisti in ${series.length} ruoli.`;
  } else if (multiCompare) {
    const totalPoints = series.reduce((sum, row) => sum + row.points.length, 0);
    meta = `${totalPoints} punti · ${series.length} rose${filterLabel}`;
    ariaLabel = `Confronto andamento prezzi su ${totalPoints} acquisti in ${series.length} rose${filterLabel}.`;
  } else {
    const points = isGlobalTrend || !assigned ? trend.points : series[0].points;
    const last = points[points.length - 1];
    const rangeLabel = isGlobalTrend && globalTurnRange && globalTurnRange.total > 1
      ? ` · chiamate ${globalTurnRange.from}–${globalTurnRange.to}`
      : '';
    meta = isGlobalTrend
      ? `${points.length} nel tratto · media ${last.avg}M${rangeLabel}${filterLabel}`
      : `${points.length} acquisti · media ${last.avg}M${filterLabel}`;
    ariaLabel = isGlobalTrend
      ? `Andamento globale dell'asta, chiamate ${globalTurnRange?.from ?? 1}–${globalTurnRange?.to ?? points.length}${filterLabel}. Ultimo prezzo ${last.price} milioni, media cumulativa ${last.avg} milioni.`
      : `Andamento dei prezzi su ${points.length} acquisti${filterLabel}. Ultimo prezzo ${last.price} milioni, media cumulativa ${last.avg} milioni.`;
  }

  const svg = assigned && isRosaTrend
    ? buildPriceTrendSvg(trend, { opponentsOrderedIds })
    : assigned && isRolesTrend
      ? buildPriceTrendSvg(trend, {
        colorForSeries: (bucket) => colorForRoleTrendSeries(bucket, mode),
      })
      : buildPriceTrendSvg(trend);
  let legend;
  if (multiCompare && isRolesTrend) {
    legend = renderRoleSeriesLegend(roleBuckets, roleSeriesIds, mode);
  } else if (multiCompare) {
    legend = renderCompareSeriesLegend(seriesChips, selectedSeriesIds, opponentsOrderedIds);
  } else {
    legend = renderSingleSeriesLegend();
  }
  const caption = !assigned
    ? 'Asse orizzontale: ordine delle chiamate nell&apos;asta'
    : isGlobalTrend
      ? 'Asse orizzontale: numero chiamata nell&apos;asta (ordine cronologico)'
      : isRolesTrend
        ? 'Asse orizzontale: ordine degli acquisti per ruolo (turni normalizzati)'
        : 'Asse orizzontale: ordine degli acquisti della rosa';

  return `
    <section class="analytics-section analytics-section--trend" aria-labelledby="analytics-trend-title">
      <div class="analytics-section-heading">
        <h2 id="analytics-trend-title" class="analytics-section-title">Andamento prezzi</h2>
        <span class="analytics-section-meta">${escapeHtml(meta)}</span>
      </div>
      ${filters}
      ${viewSwitch}
      ${seriesFilters}
      ${roleSeriesFilters}
      ${turnRangeControl}
      <div class="analytics-trend-panel" data-testid="analytics-price-trend" aria-label="${escapeHtml(ariaLabel)}">
        ${svg}
        ${legend}
        <p class="analytics-trend-axis-caption">${caption}</p>
      </div>
    </section>
  `;
}

function badgeCopy(status) {
  if (status === 'above') return 'Sopra media';
  if (status === 'below') return 'Sotto media';
  if (status === 'equal') return 'In media';
  return 'Media n/d';
}

function renderCompare(compare, hasMarket, {
  playersLabel = 'I miei acquisti',
  focusAvgHeading = 'Media mia',
  emptyMessage = 'Il confronto apparirà dopo il primo giocatore preso da te.',
} = {}) {
  if (!hasMarket) {
    return `
      <section class="analytics-section" aria-labelledby="analytics-compare-title">
        <h2 id="analytics-compare-title" class="analytics-section-title">Confronto</h2>
        <p class="analytics-empty" data-testid="analytics-compare-empty">
          Il confronto apparirà dopo i primi acquisti registrati con prezzo.
        </p>
      </section>
    `;
  }

  if (compare.myPlayers.length === 0) {
    return `
      <section class="analytics-section" aria-labelledby="analytics-compare-title">
        <h2 id="analytics-compare-title" class="analytics-section-title">Confronto</h2>
        <p class="analytics-empty" data-testid="analytics-compare-empty">
          ${escapeHtml(emptyMessage)}
        </p>
      </section>
    `;
  }

  const table = compare.rows.length > 0
    ? renderTable({
      label: `Confronto tra ${focusAvgHeading.toLowerCase()} e quelle dell’asta`,
      headings: ['Ruolo', focusAvgHeading, 'Media asta', 'Differenza'],
      rows: compare.rows.map((row) => {
        const delta = row.delta == null
          ? 'n/d'
          : `${row.delta > 0 ? '+' : ''}${row.delta}M`;
        return `
          <tr>
            <th scope="row">${renderRoleChip(row.bucket)}</th>
            <td class="num">${formatPrice(row.myAvg)}</td>
            <td class="num">${formatPrice(row.marketAvg)}</td>
            <td class="num">${delta}</td>
          </tr>
        `;
      }),
      testId: 'analytics-compare-table',
    })
    : '<p class="analytics-inline-note">Ruoli non disponibili per il confronto.</p>';

  const players = compare.myPlayers.map((entry) => `
    <li class="analytics-my-item">
      ${renderRoleChip(entry.bucket)}
      <span class="analytics-my-name">${escapeHtml(entry.name)}</span>
      <strong class="analytics-my-price num">${formatPrice(entry.price)}</strong>
      <span class="analytics-vs analytics-vs--${entry.vs}">${badgeCopy(entry.vs)}</span>
    </li>
  `).join('');

  return `
    <section class="analytics-section" aria-labelledby="analytics-compare-title">
      <div class="analytics-section-heading analytics-section-heading--split">
        <h2 id="analytics-compare-title" class="analytics-section-title">Confronto</h2>
        <span class="analytics-section-meta">${escapeHtml(playersLabel)}</span>
      </div>
      <div class="analytics-compare-grid">
        ${table}
        <div class="analytics-my-players">
          ${renderListPanel({
            headings: ['Ruolo', 'Giocatore', 'Prezzo', 'Vs media'],
            itemsHtml: players,
            listTag: 'ul',
            listClass: 'analytics-my-list',
            headModifier: 'analytics-list-head--my',
          })}
        </div>
      </div>
    </section>
  `;
}

function renderCreditBattery(row, { benchmarkPct = 0 } = {}) {
  const { pct, level, remaining, total } = creditBatteryLevel(row);
  const isMe = row.id === 'me';
  const label = total > 0
    ? `Crediti residui ${remaining} su ${total}, circa ${pct} per cento`
    : 'Nessun budget';
  const showBenchmark = Number(benchmarkPct) > 0;
  return `
    <div class="analytics-credit-battery${isMe ? ' analytics-credit-battery--me' : ''}"
      role="meter"
      aria-valuenow="${pct}"
      aria-valuemin="0"
      aria-valuemax="100"
      aria-label="${escapeHtml(label)}"
      data-testid="analytics-credit-battery-${escapeHtml(row.id)}">
      <div class="analytics-credit-battery__track">
        <div class="analytics-credit-battery__bar">
          ${showBenchmark ? `<span class="analytics-credit-battery__benchmark" style="left:${benchmarkPct}%" aria-hidden="true" data-testid="analytics-credit-benchmark"></span>` : ''}
          <span class="analytics-credit-battery__mark" style="left:25%" aria-hidden="true"><span>25%</span></span>
          <span class="analytics-credit-battery__mark" style="left:50%" aria-hidden="true"><span>50%</span></span>
          <span class="analytics-credit-battery__mark" style="left:75%" aria-hidden="true"><span>75%</span></span>
          <span class="analytics-credit-battery__pin analytics-credit-battery__pin--${level}" style="left:${pct}%" aria-hidden="true" data-testid="analytics-credit-pin"></span>
        </div>
      </div>
      <span class="analytics-credit-battery__cap" aria-hidden="true"></span>
    </div>
  `;
}

function renderPerRosaTable(rows, focusId = 'me') {
  const sorted = sortPerRosaRows(rows);
  const { pct: benchmarkPct } = richestCreditBenchmark(sorted);
  if (!sorted.length) {
    return `
      <section class="analytics-section" aria-labelledby="analytics-per-rosa-title">
        <h2 id="analytics-per-rosa-title" class="analytics-section-title">Per rosa</h2>
        <p class="analytics-inline-note">Nessuna rosa avversaria ancora.</p>
      </section>
    `;
  }
  return `
    <section class="analytics-section" aria-labelledby="analytics-per-rosa-title" data-testid="analytics-per-rosa">
      <h2 id="analytics-per-rosa-title" class="analytics-section-title">Per rosa</h2>
      <div class="analytics-table-wrap">
        <table class="analytics-table" data-testid="analytics-per-rosa-table">
          <thead>
            <tr>
              <th scope="col">Rosa</th>
              <th scope="col">Speso</th>
              <th scope="col" class="analytics-credit-col">Crediti</th>
              <th scope="col">Posti</th>
              <th scope="col">Max off.</th>
            </tr>
          </thead>
          <tbody>
            ${sorted.map((r) => {
              const isMe = r.id === 'me';
              const isFocus = r.id === focusId;
              const rowCls = `${isMe ? ' analytics-per-rosa-row--me' : ''}${isFocus ? ' analytics-per-rosa-row--focus' : ''}`;
              return `
              <tr class="analytics-per-rosa-row${rowCls}" data-testid="analytics-per-rosa-row-${escapeHtml(r.id)}">
                <th scope="row">${escapeHtml(r.name)}</th>
                <td class="num">${r.spent}M / ${r.budget}M</td>
                <td class="analytics-credit-cell">${renderCreditBattery(r, { benchmarkPct })}</td>
                <td class="num">${r.owned}/${r.maxPlayers}</td>
                <td class="num">${r.maxBid}M</td>
              </tr>
            `;
            }).join('')}
          </tbody>
        </table>
      </div>
    </section>
  `;
}

function normalizeWhoFilter(raw, opponents) {
  const value = !raw || raw === 'all' ? 'me' : raw;
  if (value === 'me') return 'me';
  if (opponents.some((o) => String(o.id) === String(value))) return value;
  return 'me';
}

function renderWhoFilter({ assigned, opponents, whoFilter }) {
  if (!assigned) return '';
  const chips = [
    { id: 'me', label: 'Tu' },
    ...opponents.map((o, i) => ({
      id: o.id,
      label: String(o.name || '').trim() || opponentPlaceholder(i),
    })),
  ];
  return `
    <div class="analytics-who-filter" data-testid="analytics-who-filter">
      <div class="analytics-who-filter-head">
        <span class="analytics-who-label" id="analytics-who-filter-label">Focus rosa</span>
        <span class="analytics-who-hint">Situazione e confronto · mercato e grafici su tutta l&apos;asta</span>
      </div>
      <div class="analytics-who-filter-chips" role="group" aria-labelledby="analytics-who-filter-label">
      ${chips.map((c) => `
        <button type="button" class="analytics-who-btn${whoFilter === c.id ? ' is-active' : ''}"
          data-analytics-who="${escapeHtml(c.id)}"
          aria-pressed="${whoFilter === c.id}">${escapeHtml(c.label)}</button>
      `).join('')}
      </div>
    </div>
  `;
}

export const analyticsViewMethods = {
  renderAnalyticsView() {
    const root = document.getElementById('analytics-root');
    if (!root) return;

    const mode = this.state.analyticsRoleMode === 'classic' ? 'classic' : 'mantra';
    this.state.analyticsRoleMode = mode;

    const assigned = this.state.rosaExitMode === EXIT_MODE_ASSIGNED;
    const opponents = Array.isArray(this.state.auctionOpponents) ? this.state.auctionOpponents : [];
    let whoFilter = normalizeWhoFilter(this.state.analyticsWhoFilter, opponents);
    if (!assigned) whoFilter = 'me';
    this.state.analyticsWhoFilter = whoFilter;

    const playersByKey = new Map(
      (this.state.allPlayers || []).map((player) => [playerToKey(player), player]),
    );
    const rosterEntries = Array.from(
      this.state.roster || [],
      ([key, entry]) => ({ key, price: entry?.price, at: entry?.at }),
    );
    const exitEntriesAll = Array.from(
      this.state.auctionExits || [],
      ([key, entry]) => ({
        key,
        price: entry?.price,
        at: entry?.at,
        opponentId: entry?.opponentId ?? null,
      }),
    ).filter((entry) => Number.isFinite(Number(entry.price)) && Number(entry.price) >= 1);

    let exitEntries = exitEntriesAll;
    let rosterForSummary = rosterEntries;
    let situationEntries = rosterEntries;
    let situationTitle = 'Situazione mia';
    let compareEntries = rosterEntries;
    let comparePlayersLabel = 'I miei acquisti';
    let compareFocusAvgHeading = 'Media mia';
    let compareEmptyMessage = 'Il confronto apparirà dopo il primo giocatore preso da te.';
    if (assigned && whoFilter === 'me') {
      exitEntries = [];
      situationEntries = rosterEntries;
      situationTitle = 'Situazione · Tu';
      compareEntries = rosterEntries;
    } else if (assigned) {
      rosterForSummary = [];
      exitEntries = exitEntriesAll.filter((e) => String(e.opponentId || '') === String(whoFilter));
      situationEntries = exitEntries;
      compareEntries = exitEntries;
      const opp = opponents.find((o) => String(o.id) === String(whoFilter));
      const oppLabel = String(opp?.name || '').trim() || 'Avversario';
      situationTitle = `Situazione · ${oppLabel}`;
      comparePlayersLabel = `Acquisti · ${oppLabel}`;
      compareFocusAvgHeading = `Media ${oppLabel}`;
      compareEmptyMessage = `Il confronto apparirà dopo il primo giocatore preso da ${oppLabel}.`;
    }

    const compareMarketEvents = collectMarketEvents({
      rosterEntries,
      exitEntries: exitEntriesAll,
    });

    const exitsMap = this.state.auctionExits instanceof Map ? this.state.auctionExits : new Map();
    const marketEvents = collectMarketEvents({
      rosterEntries,
      exitEntries: exitEntriesAll,
    }).map((e) => {
      if (exitsMap.has(e.key) && !this.state.roster?.has?.(e.key)) {
        return { ...e, opponentId: exitsMap.get(e.key)?.opponentId ?? null, source: 'exit' };
      }
      return { ...e, source: 'roster' };
    });

    const summary = computeAnalyticsSummary({
      mode,
      budget: Number(this.state.rosaBudget) || 0,
      maxPlayers: Number(this.state.rosaMaxPlayers) || 0,
      rosterEntries: rosterForSummary,
      exitEntries,
      playersByKey,
      marketEvents,
      situationEntries,
      compareEntries,
      compareMarketEvents,
    });
    const trendMarketEvents = assigned
      ? collectMarketEvents({ rosterEntries, exitEntries: exitEntriesAll })
      : marketEvents;
    const roleBuckets = listPriceTrendRoleBuckets({
      playersByKey,
      mode,
      marketEvents: trendMarketEvents,
    });
    let roleFilter = this.state.analyticsTrendRoleFilter || null;
    if (roleFilter && !roleBuckets.some((row) => row.bucket === roleFilter)) {
      roleFilter = null;
      this.state.analyticsTrendRoleFilter = null;
    }

    const opponentsOrderedIds = opponents.map((opponent) => opponent.id);
    const trendViewMode = resolveTrendViewMode(this.state, assigned);
    this.state.analyticsTrendViewMode = trendViewMode;
    let seriesChips = null;
    let selectedSeriesIds = [];
    let roleSeriesIds = [];
    let isGlobalTrend = trendViewMode === TREND_VIEW_GLOBAL;
    let priceTrend;
    let globalTurnRange = null;
    if (assigned) {
      if (isGlobalTrend) {
        priceTrend = computePriceTrend({
          rosterEntries,
          exitEntries: exitEntriesAll,
          playersByKey,
          mode,
          roleFilter,
        });
        this._analyticsGlobalTrendFull = priceTrend;
        const maxTurn = priceTrend.points[priceTrend.points.length - 1]?.index
          ?? priceTrend.totalTurns
          ?? 0;
        globalTurnRange = normalizeGlobalTurnRange(this.state.analyticsGlobalTurnRange, maxTurn);
        this.state.analyticsGlobalTurnRange = globalTurnRange;
        priceTrend = slicePriceTrend(this._analyticsGlobalTrendFull, globalTurnRange.from, globalTurnRange.to);
      } else if (trendViewMode === TREND_VIEW_ROLES) {
        this._analyticsGlobalTrendFull = null;
        this.state.analyticsGlobalTurnRange = null;
        const validRoleBuckets = roleBuckets.map((row) => row.bucket);
        roleSeriesIds = normalizeTrendRoleSeriesIds(
          this.state.analyticsTrendRoleSeriesIds,
          validRoleBuckets,
        );
        this.state.analyticsTrendRoleSeriesIds = roleSeriesIds;
        priceTrend = computePriceTrendSeriesByRole({
          rosterEntries,
          exitEntries: exitEntriesAll,
          playersByKey,
          mode,
          roleSeriesIds,
        });
      } else {
        this._analyticsGlobalTrendFull = null;
        this.state.analyticsGlobalTurnRange = null;
        seriesChips = buildTrendSeriesChips({ opponents, exitEntries: exitEntriesAll });
        selectedSeriesIds = normalizeTrendSeriesIds(
          this.state.analyticsTrendSeriesIds,
          seriesChips.map((chip) => chip.id),
        );
        this.state.analyticsTrendSeriesIds = selectedSeriesIds;
        priceTrend = computePriceTrendSeries({
          rosterEntries,
          exitEntries: exitEntriesAll,
          playersByKey,
          mode,
          roleFilter,
          seriesIds: selectedSeriesIds,
        });
      }
    } else {
      priceTrend = computePriceTrend({
        playersByKey,
        mode,
        roleFilter,
        marketEvents,
      });
    }
    const hasMarket = summary.market.top.length > 0;

    const perRosaRows = assigned
      ? [
        buildOwnSpendRow(this.state.roster, {
          budget: Number(this.state.rosaBudget) || 0,
          maxPlayers: Number(this.state.rosaMaxPlayers) || 0,
        }),
        ...buildOpponentSpendRows(
          this.state.auctionExits,
          opponents,
          {
            budget: Number(this.state.rosaBudget) || 0,
            maxPlayers: Number(this.state.rosaMaxPlayers) || 0,
          },
        ).filter((r) => !r.pile),
      ]
      : [];

    const rosterGrid = buildRosterAcquisitionGrid({
      roster: this.state.roster,
      auctionExits: this.state.auctionExits,
      opponents,
      playersByKey,
      mode,
      budget: Number(this.state.rosaBudget) || 0,
      maxPlayers: Number(this.state.rosaMaxPlayers) || 0,
      assigned,
    });

    root.innerHTML = `
      ${renderSituation(summary.situation, { title: situationTitle })}
      ${renderWhoFilter({ assigned, opponents, whoFilter })}
      ${assigned ? renderPerRosaTable(perRosaRows, whoFilter) : ''}
      ${renderRosterAcquisitionGrid(rosterGrid)}
      <div class="analytics-role-toggle" role="group" aria-label="Aggregazione ruoli">
        <button type="button" class="analytics-role-btn${mode === 'mantra' ? ' is-active' : ''}"
                data-analytics-role-mode="mantra" aria-pressed="${mode === 'mantra'}">
          Mantra
        </button>
        <button type="button" class="analytics-role-btn${mode === 'classic' ? ' is-active' : ''}"
                data-analytics-role-mode="classic" aria-pressed="${mode === 'classic'}">
          Classic
        </button>
      </div>
      ${renderPriceTrend(priceTrend, {
        roleBuckets,
        roleFilter,
        assigned,
        trendViewMode,
        seriesChips,
        selectedSeriesIds,
        roleSeriesIds,
        opponentsOrderedIds,
        globalTurnRange,
        mode,
      })}
      ${renderMarket(summary.market)}
      ${renderCompare(summary.compare, hasMarket, {
        playersLabel: comparePlayersLabel,
        focusAvgHeading: compareFocusAvgHeading,
        emptyMessage: compareEmptyMessage,
      })}
    `;

    root.querySelectorAll('[data-analytics-role-mode]').forEach((button) => {
      button.addEventListener('click', () => {
        const nextMode = button.dataset.analyticsRoleMode;
        if (nextMode === this.state.analyticsRoleMode) return;
        this.state.analyticsRoleMode = nextMode;
        this.state.analyticsTrendRoleFilter = null;
        this.renderAnalyticsView();
      });
    });

    root.querySelectorAll('[data-analytics-trend-role]').forEach((button) => {
      button.addEventListener('click', () => {
        const next = button.dataset.analyticsTrendRole || null;
        const normalized = next === '' ? null : next;
        if (normalized === (this.state.analyticsTrendRoleFilter || null)) return;
        this.state.analyticsTrendRoleFilter = normalized;
        this.renderAnalyticsView();
      });
    });

    root.querySelectorAll('[data-analytics-who]').forEach((button) => {
      button.addEventListener('click', () => {
        const next = button.dataset.analyticsWho || 'me';
        if (next === this.state.analyticsWhoFilter) return;
        this.state.analyticsWhoFilter = next;
        this.renderAnalyticsView();
      });
    });

    root.querySelectorAll('[data-analytics-trend-view]').forEach((button) => {
      button.addEventListener('click', () => {
        const next = button.dataset.analyticsTrendView;
        if (!next || next === this.state.analyticsTrendViewMode) return;
        this.state.analyticsTrendViewMode = next;
        this.renderAnalyticsView();
      });
    });

    root.querySelectorAll('[data-analytics-trend-series]').forEach((button) => {
      button.addEventListener('click', () => {
        const id = button.dataset.analyticsTrendSeries;
        const current = [...(this.state.analyticsTrendSeriesIds || [])]
          .filter((item) => item !== SERIES_ID_GLOBAL);
        const next = current.includes(id)
          ? current.filter((item) => item !== id)
          : [...current, id];
        if (next.length === 0) return;
        this.state.analyticsTrendSeriesIds = next;
        this.renderAnalyticsView();
      });
    });

    root.querySelectorAll('[data-analytics-trend-role-series]').forEach((button) => {
      button.addEventListener('click', () => {
        const bucket = button.dataset.analyticsTrendRoleSeries;
        if (!bucket) return;
        const current = [...(this.state.analyticsTrendRoleSeriesIds || [])];
        const next = current.includes(bucket)
          ? current.filter((item) => item !== bucket)
          : [...current, bucket];
        if (next.length === 0) return;
        this.state.analyticsTrendRoleSeriesIds = next;
        this.renderAnalyticsView();
      });
    });

    if (isGlobalTrend) {
      this.bindGlobalTurnRangeControls();
    }
  },

  bindGlobalTurnRangeControls() {
    const root = document.getElementById('analytics-root');
    if (!root || !this._analyticsGlobalTrendFull?.points?.length) return;

    const fromInput = root.querySelector('[data-analytics-turn-from]');
    const toInput = root.querySelector('[data-analytics-turn-to]');
    if (!fromInput || !toInput) return;

    const apply = (event) => {
      const total = Number(fromInput.max) || 1;
      let from = Number(fromInput.value);
      let to = Number(toInput.value);
      if (from > to) {
        if (event?.target === fromInput) to = from;
        else from = to;
        fromInput.value = String(from);
        toInput.value = String(to);
      }
      this.state.analyticsGlobalTurnRange = { from, to, total };
      syncTurnRangeVisuals(root, from, to, total);
      this.updateGlobalTrendPanel();
    };

    const resetBtn = root.querySelector('[data-analytics-turn-reset]');
    resetBtn?.addEventListener('click', () => {
      const total = Number(fromInput.max) || 1;
      fromInput.value = '1';
      toInput.value = String(total);
      this.state.analyticsGlobalTurnRange = { from: 1, to: total, total };
      syncTurnRangeVisuals(root, 1, total, total);
      this.updateGlobalTrendPanel();
    });

    fromInput.addEventListener('input', apply);
    toInput.addEventListener('input', apply);
    syncTurnRangeVisuals(root, Number(fromInput.value), Number(toInput.value), Number(fromInput.max));
  },

  updateGlobalTrendPanel() {
    const root = document.getElementById('analytics-root');
    const full = this._analyticsGlobalTrendFull;
    if (!root || !full?.points?.length) return;

    const range = normalizeGlobalTurnRange(
      this.state.analyticsGlobalTurnRange,
      full.points[full.points.length - 1]?.index ?? full.totalTurns,
    );
    this.state.analyticsGlobalTurnRange = range;

    const trend = slicePriceTrend(full, range.from, range.to);
    const panel = root.querySelector('[data-testid="analytics-price-trend"]');
    const meta = root.querySelector('.analytics-section--trend .analytics-section-meta');
    const roleFilter = this.state.analyticsTrendRoleFilter || null;
    const filterLabel = roleFilter ? ` · ${roleFilter}` : '';

    syncTurnRangeVisuals(root, range.from, range.to, range.total);

    const svg = buildPriceTrendSvg(trend);
    const oldSvg = panel?.querySelector('.analytics-trend-svg');
    if (oldSvg && svg) {
      oldSvg.outerHTML = svg;
    }

    if (meta && trend.points.length) {
      const last = trend.points[trend.points.length - 1];
      meta.textContent = `${trend.points.length} nel tratto · media ${last.avg}M · chiamate ${range.from}–${range.to}${filterLabel}`;
    }
    if (panel && trend.points.length) {
      const last = trend.points[trend.points.length - 1];
      panel.setAttribute(
        'aria-label',
        `Andamento globale dell'asta, chiamate ${range.from}–${range.to}${filterLabel}. Ultimo prezzo ${last.price} milioni, media cumulativa ${last.avg} milioni.`,
      );
    }
  },
};
